import { useState } from 'react';
import { Folder, ImageIcon, Paperclip } from 'lucide-react';
import { useHwcgFlag } from 'utils/featureFlags';
import {
  ASSET_IMAGE_PLACEHOLDER,
  normalizeAssetDocumentFolders,
  type AssetDocumentFolder,
} from './assetDocumentFolders';
import { AssetOrgFolderModal } from './AssetOrgFolderModal';
import {
  assetFoldersMissingNotefolderIds,
  useEnsureAssetFoldersOnOpen,
} from './useEnsureAssetOrgFolders';

type AssetMediaSectionProps = {
  imageUrl?: string | null;
  documentFolders?: AssetDocumentFolder[] | null;
  /** When true, clicking the section invokes edit (view mode). */
  onEdit?: () => void;
  /** Asset being shown; lets HWCG create its org folders on open. */
  resourceSetId?: string;
  assetName?: string;
  /** Called when org folders were created and saved on the asset. */
  onFoldersChange?: (folders: AssetDocumentFolder[]) => void;
  /** Render only the image or only the folders (defaults to both). */
  section?: 'all' | 'image' | 'folders';
};

export function AssetMediaSection({
  imageUrl,
  documentFolders,
  onEdit,
  resourceSetId,
  assetName,
  onFoldersChange,
  section = 'all',
}: AssetMediaSectionProps) {
  const showImage = section !== 'folders';
  const showFolders = section !== 'image';
  const hwcg = useHwcgFlag();
  const [ensuredFolders, setEnsuredFolders] = useState<
    AssetDocumentFolder[] | null
  >(null);
  const [openFolder, setOpenFolder] = useState<AssetDocumentFolder | null>(
    null
  );
  const propFolders = normalizeAssetDocumentFolders(documentFolders);
  const folders =
    ensuredFolders && assetFoldersMissingNotefolderIds(propFolders)
      ? ensuredFolders
      : propFolders;

  useEnsureAssetFoldersOnOpen({
    enabled: hwcg && !!resourceSetId,
    resourceSetId,
    assetName,
    folders: propFolders,
    onFoldersChange: (next) => {
      setEnsuredFolders(next);
      onFoldersChange?.(next);
    },
  });
  const src = imageUrl?.trim() || ASSET_IMAGE_PLACEHOLDER;

  return (
    <>
      <div
        role={onEdit ? 'button' : undefined}
        tabIndex={onEdit ? 0 : undefined}
        className={
          onEdit
            ? 'tw:flex tw:w-full tw:cursor-pointer tw:flex-col tw:gap-3 tw:rounded-md tw:border tw:border-transparent tw:bg-transparent tw:p-1 tw:text-left tw:hover:bg-muted'
            : 'tw:flex tw:w-full tw:flex-col tw:gap-3'
        }
        onClick={onEdit}
        onKeyDown={
          onEdit
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onEdit();
                }
              }
            : undefined
        }
      >
        {showImage ? (
          <div>
            <div className="tw:mb-1 tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:font-medium tw:text-foreground">
              <ImageIcon className="tw:size-3.5" />
              Image
            </div>
            <div className="tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-muted/30">
              <img
                src={src}
                alt={imageUrl?.trim() ? 'Asset' : 'Asset placeholder'}
                className="tw:h-40 tw:w-full tw:object-cover"
              />
            </div>
          </div>
        ) : null}
        {showFolders ? (
          <div>
            <div className="tw:mb-1 tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:font-medium tw:text-foreground">
              <Folder className="tw:size-3.5" />
              Folders
            </div>
            <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
              {folders.map((folder) => (
                <li
                  key={folder.name}
                  className="tw:rounded-md tw:border tw:border-border tw:bg-muted/20 tw:px-3 tw:py-2"
                >
                  {hwcg && folder.notefolderId ? (
                    <button
                      type="button"
                      className="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:text-sm tw:font-medium tw:underline-offset-2 tw:hover:underline"
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenFolder(folder);
                      }}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      {folder.name}
                    </button>
                  ) : (
                    <div className="tw:text-sm tw:font-medium">
                      {folder.name}
                    </div>
                  )}
                  {folder.files.length === 0 ? (
                    <p className="tw:m-0 tw:mt-1 tw:text-xs tw:text-muted-foreground">
                      No files
                    </p>
                  ) : (
                    <ul className="tw:m-0 tw:mt-1 tw:flex tw:list-none tw:flex-col tw:gap-1 tw:p-0">
                      {folder.files.map((file) => (
                        <li
                          key={file.fileId}
                          className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground"
                        >
                          <Paperclip className="tw:size-3" />
                          <span className="tw:truncate">
                            {file.label || file.fileId}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {showFolders ? (
        <AssetOrgFolderModal
          open={!!openFolder}
          onOpenChange={(next) => {
            if (!next) setOpenFolder(null);
          }}
          notefolderId={openFolder?.notefolderId ?? null}
          folderName={openFolder?.name ?? ''}
          assetName={assetName}
        />
      ) : null}
    </>
  );
}
